import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import {
  PolicyLayout,
  PolicyNotice,
  PolicySection,
} from "./Terms";
import "../css/delivery.scss";

const deliverySections = [
  { id: "shipping-overview", label: "Shipping at a glance" },
  { id: "processing", label: "Processing schedule" },
  { id: "methods-rates", label: "Methods and rates" },
  { id: "cold-chain", label: "Freshness packaging" },
  { id: "tracking-delivery", label: "Tracking and delivery" },
  { id: "address-changes", label: "Address changes" },
  { id: "delays", label: "Delays and exceptions" },
  { id: "delivery-issues", label: "Delivery issues" },
];

function Delivery() {
  return (
    <PolicyLayout
      eyebrow="Customer care · Delivery"
      intro="From pantry shelf to front door, this guide explains processing days, transit estimates, cold-chain packing, and what to do when plans change."
      modifier="delivery"
      sections={deliverySections}
      title="Shipping & delivery"
    >
      <PolicyNotice title="Shipping rate">
        <p>
          Standard delivery is charged at a flat rate of $9.99 per order.
          Available destinations are shown at checkout.
        </p>
      </PolicyNotice>

      <PolicySection id="shipping-overview" title="1. Shipping at a glance">
        <div className="policy-page__cards delivery-policy__summary">
          <article>
            <span>1–2 days</span>
            <h3>Order processing</h3>
            <p>Most in-stock orders are prepared within 1–2 business days.</p>
          </article>
          <article>
            <span>Mon–Wed</span>
            <h3>Chilled dispatch</h3>
            <p>Temperature-sensitive parcels leave early in the week to avoid weekend holds.</p>
          </article>
          <article>
            <span>At checkout</span>
            <h3>Rates and timing</h3>
            <p>Available methods depend on the basket, destination, and requested date.</p>
          </article>
          <article>
            <span>24 hours</span>
            <h3>Report an issue</h3>
            <p>Tell us promptly if a parcel arrives damaged, warm, incomplete, or incorrect.</p>
          </article>
        </div>
        <p>
          Transit estimates begin after the parcel is handed to the carrier, not
          when the order is placed. Business days exclude weekends and carrier
          holidays. The final checkout method should always take priority over a
          general estimate on this page.
        </p>
      </PolicySection>

      <PolicySection id="processing" title="2. Processing schedule">
        <p>
          Most in-stock orders are picked, checked, and packed within 1–2 business
          days. Orders placed after 11:00 a.m. local warehouse time begin processing
          the next business day. Larger gifts, multi-address orders, and peak-season
          volumes may require additional preparation time.
        </p>
        <dl className="policy-page__facts">
          <div>
            <dt>Shelf-stable orders</dt>
            <dd>Normally packed Monday through Friday.</dd>
          </div>
          <div>
            <dt>Perishable orders</dt>
            <dd>Normally dispatched Monday through Wednesday, or when transit will avoid a weekend hold.</dd>
          </div>
          <div>
            <dt>Mixed baskets</dt>
            <dd>Use the schedule required by the most temperature-sensitive item.</dd>
          </div>
          <div>
            <dt>Preorders</dt>
            <dd>Ship on or near the product-page release window; the full order may be held or split.</dd>
          </div>
        </dl>
        <p>
          A requested arrival date is a planning preference unless checkout
          explicitly labels it guaranteed. We may dispatch early enough to protect
          product quality and meet the requested window.
        </p>
      </PolicySection>

      <PolicySection id="methods-rates" title="3. Methods and rates">
        <div className="policy-page__table-wrap">
          <table className="policy-page__table">
            <thead>
              <tr>
                <th scope="col">Method</th>
                <th scope="col">Typical transit after dispatch</th>
                <th scope="col">Best for</th>
                <th scope="col">Rate</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Standard</td>
                <td>3–5 business days</td>
                <td>Eligible shelf-stable goods</td>
                <td>$9.99</td>
              </tr>
              <tr>
                <td>Chilled delivery</td>
                <td>1–2 business days</td>
                <td>Cheese, meat, and temperature-sensitive gifts</td>
                <td>Based on destination, weight, and packaging</td>
              </tr>
              <tr>
                <td>Express</td>
                <td>1 business day where available</td>
                <td>Time-sensitive eligible orders</td>
                <td>Calculated at checkout</td>
              </tr>
              <tr>
                <td>Multi-address gifting</td>
                <td>Varies by destination</td>
                <td>Sending the same occasion to several recipients</td>
                <td>Calculated for each address</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Service availability is determined by postal code, item restrictions,
          carrier coverage, weather, and the day of the week. Displayed rates may
          include insulated materials and cold packs when required. We currently
          deliver only to the destinations shown at checkout.
        </p>
      </PolicySection>

      <PolicySection id="cold-chain" title="4. Freshness packaging">
        <p>
          Temperature-sensitive products are packed to arrive cool and ready to
          refrigerate. Depending on the season, route, and contents, a live order
          may include a thermal liner, reusable gel packs, cushioning, and a faster
          carrier service.
        </p>
        <ol className="policy-page__steps">
          <li>
            <div>
              <strong>Products are checked</strong>
              <p>Packers review condition, labeling, and the appropriate storage profile.</p>
            </div>
          </li>
          <li>
            <div>
              <strong>The box is insulated</strong>
              <p>Cold packs and liners are selected for the route and expected weather.</p>
            </div>
          </li>
          <li>
            <div>
              <strong>Transit is kept short</strong>
              <p>Perishables use an eligible service designed to avoid warehouse weekends.</p>
            </div>
          </li>
          <li>
            <div>
              <strong>You refrigerate promptly</strong>
              <p>Open the parcel on arrival and follow each item&apos;s label instructions.</p>
            </div>
          </li>
        </ol>
        <PolicyNotice tone="success" title="Cool is expected; frozen is not required">
          <p>
            Gel packs may be partly or fully thawed on arrival. That alone does not
            mean food is unsafe. Check product condition and temperature, follow the
            label, and contact us if you are uncertain.
          </p>
        </PolicyNotice>
      </PolicySection>

      <PolicySection id="tracking-delivery" title="5. Tracking and delivery">
        <p>
          A production order receives tracking after the carrier scans the parcel.
          Scan activity can take several hours to appear. Tracking estimates are
          maintained by the carrier and may change during transit.
        </p>
        <ul>
          <li>Review the destination and any apartment, suite, gate, or business details before checkout.</li>
          <li>Tell a gift recipient to expect a perishable parcel, especially in warm weather.</li>
          <li>Arrange for prompt collection; do not leave food in a lobby, mailroom, vehicle, or direct sun.</li>
          <li>Refrigerate chilled items immediately and keep all packaging until you confirm the order is correct.</li>
        </ul>
        <p>
          Carrier confirmation at the submitted address counts as delivery. Signature
          service may be offered for some destinations or high-value orders.
        </p>
      </PolicySection>

      <PolicySection id="address-changes" title="6. Address changes and cancellations">
        <p>
          Contact us as soon as possible if an address or delivery plan changes. We
          can try to update an order only before packing or carrier handoff. A change
          is not confirmed until customer care replies in writing.
        </p>
        <p>
          Once a parcel is in transit, carrier redirection may be unavailable,
          delayed, or subject to a fee. We cannot guarantee recovery or replacement
          when an incorrect or incomplete address was submitted at checkout. Because
          food is packed to a time-sensitive schedule, cancellation may not be
          possible after processing begins.
        </p>
      </PolicySection>

      <PolicySection id="delays" title="7. Delays and exceptions">
        <p>
          Severe weather, carrier interruptions, holidays, high seasonal volume,
          security restrictions, and events outside reasonable control can delay a
          shipment. To protect perishable products, we may hold an order for the next
          safe dispatch day or upgrade, split, substitute, or cancel an affected
          shipment after contacting you when practical.
        </p>
        <PolicyNotice tone="warning" title="Plan ahead for an event">
          <p>
            Choose an arrival date at least one day before a celebration whenever
            possible. Most cheeses, cured meats, and shelf-stable accompaniments keep
            well when stored according to their labels.
          </p>
        </PolicyNotice>
      </PolicySection>

      <PolicySection id="delivery-issues" title="8. Delivery issues">
        <p>
          If an order is missing, incorrect, visibly damaged, or arrives with a
          freshness concern, contact us within 24 hours. Include the order number,
          delivery date, a description, and photos of the item, label, shipping box,
          and thermal materials when relevant.
        </p>
        <p>
          Email <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>,
          or use our <Link to="/contact">contact form</Link>.
          Review the <Link to="/returns">returns and quality policy</Link> for remedy
          eligibility and safe-handling guidance.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}

export default Delivery;
